import { type SxProps, type Theme } from '@mui/material';
import { format } from '@qdrant-cloud-ui/date/format';
import { type ClusterValid } from '@qdrant/qdrant-cloud-public-api/cluster/v1';
import { type ClusterNodeUsageMetrics, type Metric } from '@qdrant/qdrant-cloud-public-api/monitoring/v1';
import { type ApexOptions, type default as ApexCharts } from 'apexcharts';
import { type ClusterNodeResourcesSummaryDto } from '@/api/services/cluster';
import { type DeepOmitProtobufInternals } from '@/connect-query';
import { getClusterEndpoint, isClusterHybridCloud } from '@/utils/cluster-utils';
import { isAtLeastVersion } from '@/utils/helpers';
import {
  type ByteSizeFormatterOptions,
  formatBytesToGbytes,
  formatFloatWithSuffix,
  getNodeResourceTotal,
  transformGibibytesToBytes,
} from '../helpers';

export type ApexAxisChartSeries = Exclude<Exclude<ApexOptions['series'], undefined>, number[]>;

export type NodeUsageMetricsKeys = keyof DeepOmitProtobufInternals<ClusterNodeUsageMetrics>;

export enum MetricKeys {
  CPU = 'cpu',
  DISK = 'disk',
  GPU = 'gpu',
  GPU_RAM = 'gpuRam',
  LATENCY = 'latency',
  RAM = 'ram', // RAM including cache
  RAM_CACHE = 'ramCache',
  RAM_QDRANT_RSS = 'ramQdrantRss', // RAM without cache for Hybrid Cloud clusters
  RPS = 'rps',
  RSS = 'ramRss', // RAM without cache
}

export const RAM_LABEL = 'RAM (incl. caches)';

export const NO_METRICS_AVAILABLE_MSG = 'No metrics found for this time range. Try selecting a different period.';

export const CHART_HEIGHT = 200;
// Add a minimum height to prevent the page to jump when loading new graphs
export const CHART_CARD_HEIGHT = CHART_HEIGHT + 35;

export const chartCardStyle: SxProps = {
  minHeight: CHART_CARD_HEIGHT,
  p: 2,
};

export const CHART_COLORS = {
  BLUE: '#1F87E6',
  GREEN: '#4CAF50',
  ORANGE: '#FF9800',
  PINK: '#FF5C7C',
  RED: '#F44336',
  YELLOW: '#FAD107',
};

// Date format constants for chart axis labels
const DATE_AND_TIME_FORMAT = 'd MMM yy p';
const TIME_FORMAT = 'p';
const TIME_FORMAT_WITH_SECONDS = 'pp';
export const DATE_FORMAT = 'd MMM p';

// Time duration constants in seconds for format selection
const TIME_THRESHOLDS = {
  HOUR: 60 * 60,
  DAY: 24 * 60 * 60,
  WEEK: 7 * 24 * 60 * 60,
} as const;

/**
 * Determines the optimal date format based on time range duration.
 * Pre-calculates format to avoid repeated calculations during chart rendering.
 *
 * @param timeRangeInSeconds - Duration of the time range in seconds
 * @param showTime - Whether to show time information (for resource charts with minute precision)
 * @returns Date format string optimized for the time range
 */
const getOptimalDateFormat = (timeRangeInSeconds: number, showTime = true) => {
  // For charts that don't need time precision (like token charts with daily data)
  if (!showTime) {
    return DATE_FORMAT;
  }

  if (timeRangeInSeconds <= TIME_THRESHOLDS.HOUR) {
    // Short ranges (≤1h): show precise time with seconds (e.g., "14:30:45")
    return TIME_FORMAT_WITH_SECONDS;
  }

  if (timeRangeInSeconds <= TIME_THRESHOLDS.DAY) {
    // Medium ranges (≤1d): show time without seconds (e.g., "14:30")
    return TIME_FORMAT;
  }

  if (timeRangeInSeconds <= TIME_THRESHOLDS.WEEK) {
    // Long ranges (≤1w): show date and time (e.g., "15 Jan 23 14:30")
    return DATE_AND_TIME_FORMAT;
  }

  // Very long ranges (>1w): show date only (e.g., "15 Jan 2:30 PM")
  return DATE_FORMAT;
};

/**
 * Creates an optimized date formatter for chart X-axis labels.
 * Pre-calculates the format based on time range to avoid repeated calculations.
 *
 * @param since - Range start timestamp in seconds
 * @param until - Range end timestamp in seconds
 * @param showTime - Whether to show time information (default: true for resource charts)
 * @returns Formatter function compatible with ApexCharts that takes string and returns formatted string
 */
export const createDateFormatter = (since: number, until: number, showTime = true) => {
  const timeRangeInSeconds = (until - since) / 1000;
  const dateFormat = getOptimalDateFormat(timeRangeInSeconds, showTime);

  return (value: string) => {
    const timestamp = Number(value);
    if (!timestamp) {
      return '';
    }

    return format(timestamp, dateFormat);
  };
};

const getCoreFormatter =
  (fixedTo = 5) =>
  (value: number) =>
    formatFloatWithSuffix({ value, fixedTo });

const getUnitFormatter = (formatterOptions: ByteSizeFormatterOptions) => (value: number) =>
  humanReadableSizeFormatter(value, formatterOptions);

const getGiBFormatter = (formatterOptions: ByteSizeFormatterOptions) => (value: number) =>
  formatBytesToGbytes(value, formatterOptions);

export function humanReadableSizeFormatter(
  bytes: number,
  { standard = 'IEC', decimalPlaces = 1 }: ByteSizeFormatterOptions = {},
) {
  if (bytes > Number.MAX_SAFE_INTEGER) {
    throw new RangeError('The number is too big to be represented as a JavaScript number.');
  }
  const threshold = standard === 'SI' ? 1000 : 1024;

  if (Math.abs(bytes) < threshold) {
    return `${bytes} B`;
  }

  const units = standard === 'SI' ? ['KB', 'MB', 'GB', 'TB'] : ['KiB', 'MiB', 'GiB', 'TiB'];
  let unitIndex = -1;
  const rounding = 10 ** decimalPlaces;

  do {
    bytes /= threshold;
    ++unitIndex;
  } while (Math.round(Math.abs(bytes) * rounding) / rounding >= threshold && unitIndex < units.length - 1);

  return `${bytes.toFixed(decimalPlaces)} ${units[unitIndex]}`;
}

export const getDashboardUrl = (cluster?: ClusterValid): URL | '' => {
  if (!cluster) {
    return '';
  }
  const clusterVersion = cluster.state?.version;
  if (!isAtLeastVersion('1.3', clusterVersion)) {
    return '';
  }

  const clusterUrl = getClusterEndpoint(cluster);

  if (!clusterUrl || (isClusterHybridCloud(cluster) && clusterUrl.url.endsWith('.svc:6333'))) {
    return '';
  }

  let url: URL;
  try {
    url = new URL('/dashboard', clusterUrl.url);
  } catch {
    // If we fail to construct the URL due to an empty or malformed clusterUrl return ''
    return '';
  }
  url.port = clusterUrl.restPort.toString();
  return url;
};

const RANGE_DECIMAL_PLACES = 2;

export type MetricValues = [number, number][];
export type Metrics = {
  [MetricKeys.LATENCY]: MetricValues;
  [MetricKeys.RPS]: MetricValues;
};

type MetricDetail = {
  label: string;
  sideLabel?: string;
  formatter: (value: number) => string;
  rangeFormatter?: (value: number) => string;
};

type MetricsDetails = Record<MetricKeys, MetricDetail>;

export const METRICS_DETAILS: MetricsDetails = {
  [MetricKeys.CPU]: {
    label: 'CPU',
    formatter: getCoreFormatter(),
    rangeFormatter: getCoreFormatter(RANGE_DECIMAL_PLACES),
  },
  [MetricKeys.RAM]: {
    label: RAM_LABEL,
    sideLabel: 'RAM',
    formatter: getUnitFormatter({ decimalPlaces: 2 }),
    rangeFormatter: getGiBFormatter({ decimalPlaces: RANGE_DECIMAL_PLACES, standard: 'IEC' }),
  },
  [MetricKeys.RSS]: {
    label: 'RAM',
    formatter: getUnitFormatter({ decimalPlaces: 2 }),
    rangeFormatter: getGiBFormatter({ decimalPlaces: RANGE_DECIMAL_PLACES, standard: 'IEC' }),
  },
  [MetricKeys.RAM_QDRANT_RSS]: {
    label: 'RAM',
    formatter: getUnitFormatter({ decimalPlaces: 2 }),
    rangeFormatter: getGiBFormatter({ decimalPlaces: RANGE_DECIMAL_PLACES, standard: 'IEC' }),
  },
  [MetricKeys.RAM_CACHE]: {
    label: 'RAM',
    formatter: getUnitFormatter({ decimalPlaces: 2 }),
    rangeFormatter: getGiBFormatter({ decimalPlaces: RANGE_DECIMAL_PLACES, standard: 'IEC' }),
  },
  [MetricKeys.DISK]: {
    label: 'Disk',
    formatter: getUnitFormatter({ decimalPlaces: 2 }),
    rangeFormatter: getGiBFormatter({ decimalPlaces: RANGE_DECIMAL_PLACES, standard: 'IEC' }),
  },
  [MetricKeys.RPS]: {
    label: 'Requests',
    formatter: (value: number) => formatFloatWithSuffix({ value, fixedTo: 2, suffix: 'rps' }),
  },
  [MetricKeys.LATENCY]: {
    label: 'Latency',
    formatter: (value: number) => formatFloatWithSuffix({ value, fixedTo: 2, suffix: 'ms' }),
  },
  [MetricKeys.GPU]: {
    label: 'GPU',
    formatter: (value: number) => formatFloatWithSuffix({ value, fixedTo: 2, suffix: '%' }),
    rangeFormatter: (value: number) => formatFloatWithSuffix({ value, fixedTo: 1, suffix: '%' }),
  },
  [MetricKeys.GPU_RAM]: {
    label: 'GPU RAM',
    sideLabel: 'VRAM',
    formatter: getUnitFormatter({ decimalPlaces: 2 }),
    rangeFormatter: getGiBFormatter({ decimalPlaces: RANGE_DECIMAL_PLACES, standard: 'IEC' }),
  },
};

/**
 * ApexCharts doesn't provide a type for the options object in the custom tooltip function.
 * So we need to define it here based on usage and guessing after debugging.
 */
type TooltipOptions = {
  series: number[][];
  seriesIndex: number;
  dataPointIndex: number;
  w: {
    globals: {
      colors: string[];
    };
  };
};

const getCustomTooltipFunc =
  (metrics: ChartMetricData[], dataSeries: ApexAxisChartSeries) => (options: TooltipOptions) => {
    const { series, dataPointIndex, w } = options;
    // Calculate the total value at the given data point
    const total: number = series.reduce((acc: number, curr: number[]) => acc + curr[dataPointIndex], 0);

    // Create the custom tooltip HTML (by opening the wrapper div)
    let tooltipHtml = `<div>`;

    // Add each metric value to the tooltip
    series.forEach((s: number[], i: number) => {
      const value = s[dataPointIndex];
      const formattedValue = METRICS_DETAILS[metrics[i].name].formatter(value);

      tooltipHtml += `
      <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
        <span class="apexcharts-tooltip-marker" style="background-color: ${w.globals.colors[i]};"></span>
        <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
          <div class="apexcharts-tooltip-y-group">
            <span class="apexcharts-tooltip-text-y-label">${dataSeries[i].name}</span>
            <span class="apexcharts-tooltip-text-y-value">${formattedValue}</span>
          </div>
        </div>
      </div>`;
    });

    // Add total usage to the tooltip
    tooltipHtml += `
    <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
      <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
        <div class="apexcharts-tooltip-y-group">
          <span class="apexcharts-tooltip-text-y-label">Total:</span>
          <span class="apexcharts-tooltip-text-y-value">${METRICS_DETAILS[metrics[0].name].formatter(total)}</span>
        </div>
      </div>
    </div>`;

    // Close the tooltip wrapper div
    tooltipHtml += '</div>';

    return tooltipHtml;
  };

export type ChartMetricData = {
  name: MetricKeys;
  label?: string;
  color: string;
  max: number;
  hideLeftBar?: boolean;
};
type ChartOptions = {
  metrics: ChartMetricData[];
  xAxisRange: {
    since: number;
    until: number;
  };
  theme: Theme;
  stacked?: boolean;
  customTooltip?: {
    dataSeries: ApexAxisChartSeries;
  };
  showTimeInLabels?: boolean;
};

/**
 * Creates an Apex charts options object.
 */
export const getChartOptions = (options: ChartOptions): ApexOptions => {
  const {
    metrics,
    xAxisRange: { since, until },
    theme,
    stacked,
    customTooltip,
    showTimeInLabels = true,
  } = options;
  return {
    chart: {
      background: 'transparent',
      events: {
        // This handler is required to fix sizing issue on Chrome/linux.
        // Taken from: https://github.com/apexcharts/react-apexcharts/issues/187
        // The change should be invisible to the users that were not affected by the problem.
        beforeMount(chart: ApexCharts & { windowResizeHandler: () => void }) {
          chart.windowResizeHandler();
        },
      },
      stacked,
      toolbar: {
        show: false,
        tools: {
          zoom: false,
          selection: false,
        },
      },
      type: 'area',
      zoom: {
        enabled: false,
      },
    },
    colors: metrics.map(({ color }) => color),
    dataLabels: {
      enabled: false,
    },
    grid: {
      borderColor: theme.palette.divider,
      yaxis: {
        lines: {
          show: false,
        },
      },
    },
    legend: {
      show: true,
    },
    noData: {
      text: 'No data available',
      align: 'center',
      verticalAlign: 'middle',
      offsetX: 0,
      offsetY: 0,
    },
    stroke: {
      curve: 'straight',
      lineCap: 'butt',
      width: 3,
    },
    theme: {
      mode: theme.palette.mode,
    },
    tooltip: !customTooltip
      ? {
          y: metrics.map(({ name }) => ({
            formatter: METRICS_DETAILS[name].formatter,
          })),
        }
      : {
          custom: getCustomTooltipFunc(metrics, customTooltip.dataSeries),
        },
    xaxis: {
      axisBorder: {
        color: theme.palette.divider,
      },
      axisTicks: {
        color: theme.palette.divider,
        show: true,
      },
      labels: {
        formatter: createDateFormatter(since, until, showTimeInLabels),
        style: {
          colors: theme.palette.text.secondary,
        },
        rotate: 0,
      },
      max: until,
      min: since,
      type: 'datetime',
    },
    yaxis: metrics.map(({ name, max, hideLeftBar }, index) =>
      Object.assign({ show: !hideLeftBar }, index % 2 !== 0 ? { opposite: true } : {}, {
        axisBorder: { color: theme.palette.divider, show: true },
        axisTicks: { color: theme.palette.divider, show: true },
        labels: {
          // Define a formatter for the Y axis range labels.
          formatter: (value: number) => {
            const rangeFormatter = METRICS_DETAILS[name].rangeFormatter ?? METRICS_DETAILS[name].formatter;
            return rangeFormatter(value);
          },
          style: { colors: theme.palette.text.secondary },
          minWidth: 40,
        },
        max,
        min: 0,
        tickAmount: 4,
        title: { text: METRICS_DETAILS[name].sideLabel ?? METRICS_DETAILS[name].label },
      }),
    ),
  };
};

/**
 * Obtains the maximum possible number for the metrics, based on the cluster config.
 */
export const getMaxResources = (resources?: ClusterNodeResourcesSummaryDto) => {
  const maxCPU = getNodeResourceTotal(resources?.[MetricKeys.CPU]);
  const maxRAM = getNodeResourceTotal(resources?.[MetricKeys.RAM]);
  const maxDisk = getNodeResourceTotal(resources?.[MetricKeys.DISK]);

  return {
    [MetricKeys.CPU]: maxCPU ? maxCPU / 1000 : maxCPU,
    // We transform the values from Gibibytes, instead of Gigabytes for total/max resources
    // because it is the unit used in the cluster resources.
    [MetricKeys.DISK]: maxDisk ? transformGibibytesToBytes(maxDisk) : maxDisk,
    [MetricKeys.RAM]: maxRAM ? transformGibibytesToBytes(maxRAM) : maxRAM,
  };
};

/**
 * Formatting uses the default byte to GiBi conversion (IEC) and 2 decimal places unless specified differently.
 */
export function formatMetricAmount(type: NodeUsageMetricsKeys, metric: number, decimalPlaces = 2): string {
  if (!Number.isFinite(metric)) {
    return 'N/A';
  }
  if (type === 'cpu') {
    return `${parseFloat(metric.toFixed(decimalPlaces))} vCPUs`;
  }
  return humanReadableSizeFormatter(metric, { decimalPlaces });
}

export type TimeRange = { since: number; until: number };

export const getMetricMax = (metricData: [number, number][]): number => {
  const values = metricData.map(([, value]) => value);
  const maxVal = Math.max(...values);
  if (!isFinite(maxVal) || maxVal <= 0) {
    return 50;
  }
  const magnitude = Math.pow(10, Math.floor(Math.log10(maxVal)));
  return Math.ceil(maxVal / magnitude) * magnitude;
};

/**
 * Computes the maximum Y value from an ApexCharts series.
 * For stacked charts, returns the maximum sum of all series at a single data point.
 * For non-stacked charts, returns the maximum individual value.
 */
export const getSeriesMax = (series: ApexAxisChartSeries, stacked = false): number => {
  if (!series.length) {
    return 0;
  }
  const dataArrays = series.map((s) => (s.data as [number, number][]).map(([, value]) => value));
  const length = dataArrays[0]?.length ?? 0;
  if (length === 0) {
    return 0;
  }
  let maxVal = 0;
  for (let i = 0; i < length; i++) {
    const pointVal = stacked
      ? dataArrays.reduce((sum, arr) => sum + (arr[i] ?? 0), 0)
      : Math.max(...dataArrays.map((arr) => arr[i] ?? 0));
    maxVal = Math.max(maxVal, pointVal);
  }
  return maxVal;
};

/**
 * Converts protobuf Metric array to chart-compatible format [timestamp, value][]
 */
export const convertMetricsToChartData = (metricData: Metric[]): MetricValues =>
  metricData.map((metricPoint) => {
    const timestamp = metricPoint.timestamp ? Number(metricPoint.timestamp.seconds) * 1000 : 0;
    const value = metricPoint.value ?? 0;
    return [timestamp, value];
  });

/**
 * Extracts the actual time range (first and last timestamp in ms) from an ApexCharts series.
 * Returns null when no data points are present.
 */
export const getSeriesTimeRange = (series: ApexAxisChartSeries): { since: number; until: number } | null => {
  const timestamps = series.flatMap((s) => (s.data as [number, number][]).map(([t]) => t)).filter((t) => t > 0);
  if (timestamps.length === 0) {
    return null;
  }
  return { since: Math.min(...timestamps), until: Math.max(...timestamps) };
};
